import { isBillingPeriod, isPaidTier } from '../config/tiers';
import type { CheckoutSelection } from '../config/tiers';

// Bridges the landing page's plan choice to the in-app Stripe checkout. Router
// state can't survive the logged-out /login redirect (RequireAuth replaces it),
// so the pricing card's CTA stashes the selection in localStorage; once the user
// is authenticated on /app, the app consumes it and — for annual — starts
// checkout. All access is try/catch-guarded so private browsing / disabled
// storage silently degrades to the plain in-app upgrade flow.

const KEY = 'mcp_pending_plan';
const MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

/** What the landing page stashed: which tier, billed how often. */
export type PendingPlan = CheckoutSelection;

/** Stash the landing-page selection (with a timestamp) for pickup after auth.
 *  No-op if storage is unavailable. */
export function setPendingPlan(selection: PendingPlan): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ tier: selection.tier, period: selection.period, ts: Date.now() })
    );
  } catch {
    /* private mode / storage disabled — degrade to no bridge */
  }
}

/**
 * One-shot read: returns the stashed selection and clears the key. Returns null
 * (and still clears) if missing, malformed, or older than an hour. Safe when
 * storage is unavailable.
 *
 * Also reads the LEGACY shape `{ plan: 'monthly' | 'annual' }`, written before
 * tiers existed. A value stashed by the previous deploy can still be sitting in
 * a user's localStorage when the new code loads — the TTL is an hour, so the
 * window is narrow but real — and the only tier that could have been meant is
 * Pro, because it was the only one for sale.
 */
export function consumePendingPlan(): PendingPlan | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY); // one-shot: always clear on read
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { tier?: unknown; period?: unknown; plan?: unknown; ts?: unknown };
    const { tier, period, plan, ts } = parsed;

    // Expiry first: a stale selection should not start a checkout the user has
    // long since forgotten about, in either shape.
    if (typeof ts !== 'number' || !isFinite(ts) || Date.now() - ts > MAX_AGE_MS) return null;

    // Current shape.
    if (isPaidTier(tier) && isBillingPeriod(period)) return { tier, period };

    // Legacy shape — period only, tier implicitly Pro.
    if (isBillingPeriod(plan)) return { tier: 'pro', period: plan };

    return null;
  } catch {
    return null;
  }
}
